/**
 * Iter XX.10 — Abandoned Gully Plus tracking beacons.
 *
 * Fires two client-side events so the founder's Abandoned Plus dashboard
 * can measure how long players spent on Stripe Checkout before leaving,
 * without pinging Stripe's servers:
 *
 *   · plus_checkout_page_opened  — fired immediately when the user
 *                                  triggers the redirect to Stripe.
 *   · plus_checkout_page_left    — fired when the user returns to our
 *                                  origin (any /plus* page) OR when the
 *                                  stash is reconciled on mount. Payload
 *                                  carries { duration_seconds }.
 *
 * State lives in localStorage under `gully_checkout_open_stash` so it
 * survives the redirect to Stripe. Capped to 3h — anything longer is
 * treated as "closed the tab and never came back" and clamped.
 */
import api from "./api";

const STASH_KEY = "gully_checkout_open_stash";
const MAX_DURATION_SECONDS = 3 * 60 * 60; // 3h clamp

function readStash() {
  try {
    const raw = localStorage.getItem(STASH_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearStash() {
  try { localStorage.removeItem(STASH_KEY); } catch { /* ignore */ }
}

/**
 * Called immediately before we redirect the user to Stripe Checkout.
 * Fires the "opened" beacon (server records timestamp) and stashes the
 * local start time so we can compute duration on return.
 */
export function markCheckoutOpened({ sessionId = null, source = "plus_page" } = {}) {
  try {
    localStorage.setItem(STASH_KEY, JSON.stringify({
      opened_at: Date.now(),
      source,
    }));
  } catch { /* ignore */ }
  try {
    api.post("/plus/track", {
      kind: "plus_checkout_page_opened",
      session_id: sessionId,
      source,
    }).catch(() => {});
  } catch { /* ignore */ }
}

/**
 * Called when the user succeeded (landed on /plus/success). Clears the
 * stash silently — no "abandoned" beacon fires.
 */
export function markCheckoutSucceeded() {
  clearStash();
}

/**
 * Called when any /plus route mounts. If there's a stash (i.e. the user
 * previously clicked "start checkout" but hasn't succeeded), fire the
 * "left" beacon with the measured duration and clear the stash.
 *
 * `isPlus` — pass the current billing state. When true, we treat this
 * as a success (silently clear).
 */
export function reconcileCheckoutReturn({ isPlus = false, sessionId = null } = {}) {
  const stash = readStash();
  if (!stash || !stash.opened_at) return;
  if (isPlus) {
    clearStash();
    return;
  }
  let durationSeconds = Math.floor((Date.now() - Number(stash.opened_at)) / 1000);
  if (!Number.isFinite(durationSeconds) || durationSeconds < 0) {
    clearStash();
    return;
  }
  if (durationSeconds > MAX_DURATION_SECONDS) durationSeconds = MAX_DURATION_SECONDS;
  try {
    api.post("/plus/track", {
      kind: "plus_checkout_page_left",
      session_id: sessionId,
      source: stash.source || "plus_page",
      payload: { duration_seconds: durationSeconds },
    }).catch(() => {});
  } catch { /* ignore */ }
  clearStash();
}
